import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, FilterBar, FilterField, Input, RadixSelect } from '@/components/ui';
import type {
  PaymentMethod,
  PaymentStatus,
} from '@/lib/api-admin/types';
import { usePaymentFiltersStore, type PaymentProviderFilter } from '@/stores/usePaymentFiltersStore';

const DEBOUNCE_MS = 300;

// QA-625 결제상태: 승인대기·결제진행중·환불실패·기한만료 제외(신규·이관 모두 없음).
export const PAYMENT_STATUS_OPTIONS: ReadonlyArray<PaymentStatus> = [
  'INITIATED',
  'CAPTURED',
  'FAILED',
  'CANCELED',
  'PARTIALLY_REFUNDED',
  'REFUNDED',
];

// QA-357: 무통장입금 — payment #7 부터 무통장 주문(FE·수기)이 결제 기록(BANK_TRANSFER)을 남기고 목록 필터를 지원한다.
// 이관 무통장은 이관팀 정정 전까지 가상계좌로 남아 있다.
export const PAYMENT_METHOD_OPTIONS: ReadonlyArray<PaymentMethod> = [
  'CARD',
  'VIRTUAL_ACCOUNT',
  'BANK_TRANSFER',
  'KAKAOPAY',
  'TOSSPAY',
];

/** payment #14 — KAKAO 카카오페이 · MANUAL 수기 결제 · TOSS 그 밖의 모든 결제(토스·이관 포함). */
const PROVIDER_OPTIONS: ReadonlyArray<PaymentProviderFilter> = ['TOSS', 'KAKAO', 'MANUAL'];

export const PaymentFiltersBar = () => {
  const { t } = useTranslation('payments');

  const keyword = usePaymentFiltersStore((s) => s.keyword);
  const paymentStatus = usePaymentFiltersStore((s) => s.paymentStatus);
  const paymentMethod = usePaymentFiltersStore((s) => s.paymentMethod);
  const provider = usePaymentFiltersStore((s) => s.provider);
  const fromDate = usePaymentFiltersStore((s) => s.fromDate);
  const toDate = usePaymentFiltersStore((s) => s.toDate);
  const hasRefund = usePaymentFiltersStore((s) => s.hasRefund);
  const excludeLegacy = usePaymentFiltersStore((s) => s.excludeLegacy);

  const setKeyword = usePaymentFiltersStore((s) => s.setKeyword);
  const setPaymentStatus = usePaymentFiltersStore((s) => s.setPaymentStatus);
  const setPaymentMethod = usePaymentFiltersStore((s) => s.setPaymentMethod);
  const setProvider = usePaymentFiltersStore((s) => s.setProvider);
  const setFromDate = usePaymentFiltersStore((s) => s.setFromDate);
  const setToDate = usePaymentFiltersStore((s) => s.setToDate);
  const setHasRefund = usePaymentFiltersStore((s) => s.setHasRefund);
  const setExcludeLegacy = usePaymentFiltersStore((s) => s.setExcludeLegacy);
  const reset = usePaymentFiltersStore((s) => s.reset);

  const [local, setLocal] = useState(keyword);

  useEffect(() => {
    if (local === keyword) return;
    const timer = setTimeout(() => setKeyword(local), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [local, keyword, setKeyword]);

  const allStatus = t('payments.filters.statusAll');

  const paymentStatusOptions = [
    { value: '', label: allStatus },
    ...PAYMENT_STATUS_OPTIONS.map((s) => ({ value: s, label: t(`payments.paymentStatus.${s}`) })),
  ];
  const paymentMethodOptions = [
    { value: '', label: t('payments.filters.methodAll') },
    ...PAYMENT_METHOD_OPTIONS.map((m) => ({ value: m, label: t(`payments.paymentMethod.${m}`) })),
  ];
  const toggleProvider = (p: PaymentProviderFilter, on: boolean) =>
    setProvider(on ? PROVIDER_OPTIONS.filter((x) => x === p || provider.includes(x)) : provider.filter((x) => x !== p));
  // 환불: 전체(생략) / 있음(true) / 없음(false)
  const refundOptions = [
    { value: '', label: t('payments.filters.hasRefundAll') },
    { value: 'true', label: t('payments.filters.hasRefundYes') },
    { value: 'false', label: t('payments.filters.hasRefundNo') },
  ];

  return (
    <FilterBar
      aria-label={t('payments.filters.title')}
      data-testid="payments-filters"
      actions={
        <Button variant="ghost" size="sm" onClick={() => { reset(); setLocal(''); }}>
          {t('payments.actions.resetFilters')}
        </Button>
      }
      filters={
        <>
          <FilterField label={t('payments.filters.keyword')}>
            <Input
              id="payment-q"
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              placeholder={t('payments.filters.keywordPlaceholder')}
              className="w-full"
              maxLength={100}
            />
          </FilterField>
          <FilterField label={t('payments.filters.paymentStatus')}>
            <RadixSelect
              id="payment-status"
              value={paymentStatus ?? ''}
              onChange={(v) => setPaymentStatus(v === '' ? null : (v as PaymentStatus))}
              options={paymentStatusOptions}
              className="w-full"
            />
          </FilterField>
          <FilterField label={t('payments.filters.paymentMethod')}>
            <RadixSelect
              id="payment-method"
              value={paymentMethod ?? ''}
              onChange={(v) => setPaymentMethod(v === '' ? null : (v as PaymentMethod))}
              options={paymentMethodOptions}
              className="w-full"
            />
          </FilterField>
          <FilterField label={t('payments.filters.provider')}>
            {/* 복수 선택(OR). 아무것도 안 고르면 전체. */}
            <div className="flex h-8 flex-wrap items-center gap-x-3 gap-y-1 text-[12px]" data-testid="payment-provider">
              {PROVIDER_OPTIONS.map((p) => (
                <label key={p} className="inline-flex cursor-pointer items-center gap-1.5 text-[color:var(--content-default)]">
                  <input
                    type="checkbox"
                    checked={provider.includes(p)}
                    onChange={(e) => toggleProvider(p, e.target.checked)}
                    data-testid={`payment-provider-${p}`}
                  />
                  {t(`payments.provider.${p}`)}
                </label>
              ))}
            </div>
          </FilterField>
          <FilterField label={t('payments.filters.fromDate')}>
            <Input
              id="payment-from"
              type="date"
              value={fromDate ?? ''}
              onChange={(e) => setFromDate(e.target.value === '' ? null : e.target.value)}
              className="w-full"
            />
          </FilterField>
          <FilterField label={t('payments.filters.toDate')}>
            <Input
              id="payment-to"
              type="date"
              value={toDate ?? ''}
              onChange={(e) => setToDate(e.target.value === '' ? null : e.target.value)}
              className="w-full"
            />
          </FilterField>
          {/* 체크박스 필터는 문구를 체크박스 옆에만 둔다 — 왼쪽 라벨 칸(72px·nowrap)에 같은 문구를 또 쓰면 넘쳐서 겹친다. 빈 라벨 칸이 남아 다른 필터와 줄은 맞는다. */}
          <FilterField label={t('payments.filters.hasRefund')}>
            <RadixSelect
              id="payment-has-refund"
              value={hasRefund === null ? '' : String(hasRefund)}
              onChange={(v) => setHasRefund(v === '' ? null : v === 'true')}
              options={refundOptions}
              className="w-full"
              data-testid="payment-has-refund"
            />
          </FilterField>
          {/* 고도몰 이관 결제(고도몰 PG 에서 이미 정산 완료)를 뺀다. 기본은 포함 — 대시보드 미정산 카드에서 오면 체크된다. */}
          <FilterField label="">
            <label
              htmlFor="payment-exclude-legacy"
              className="inline-flex h-8 cursor-pointer items-center gap-2 text-[12px] text-[color:var(--content-default)]"
            >
              <input
                id="payment-exclude-legacy"
                type="checkbox"
                checked={excludeLegacy === true}
                onChange={(e) => setExcludeLegacy(e.target.checked ? true : null)}
                data-testid="payment-exclude-legacy"
              />
              {t('payments.filters.excludeLegacy', '이관 결제 제외')}
            </label>
          </FilterField>
        </>
      }
    />
  );
};
